import { toast } from "sonner";
import {
  disableLocationsLoading,
  setCountries,
  setLocationsLoading,
  setStates,
} from "../../store/slices/locations";
import { AppDispatch } from "../../store/store";
import {
  ICountry,
  ICountriesResponse,
  IStatesResponse,
} from "../../store/types/locations";
import axiosInstance from "../../utility/axios";
import { getErrorMessage } from "../apiError";

const isCountry = (value: unknown): value is ICountry =>
  value !== null &&
  typeof value === "object" &&
  (typeof (value as ICountry).id === "number" ||
    typeof (value as ICountry).id === "string") &&
  typeof (value as ICountry).name === "string" &&
  typeof (value as ICountry).is_active === "boolean";

const normalizeCountries = (value: unknown): ICountry[] => {
  if (Array.isArray(value)) {
    const countries = value.filter(isCountry);
    if (countries.length === value.length) return countries;
    throw new Error("Countries response contains invalid country entries");
  }

  if (value && typeof value === "object") {
    const data = value as Record<string, unknown>;
    for (const key of ["countries", "items", "data"]) {
      if (data[key] !== undefined) {
        return normalizeCountries(data[key]);
      }
    }
  }

  throw new Error("Countries response did not contain a country list");
};

export const get_countries = async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setLocationsLoading());
  try {
    const response = await axiosInstance.get<ICountriesResponse>(
      "/api/v1/locations/countries",
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load countries");
    }
    dispatch(setCountries(normalizeCountries(response.data.data)));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load countries"));
  } finally {
    dispatch(disableLocationsLoading());
  }
};

export const get_states = async (
  dispatch: AppDispatch,
  countryId: string | number,
): Promise<void> => {
  dispatch(setLocationsLoading());
  try {
    const response = await axiosInstance.get<IStatesResponse>(
      `/api/v1/locations/countries/${countryId}/states`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load states");
    }
    dispatch(setStates(response.data.data.states));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load states"));
  } finally {
    dispatch(disableLocationsLoading());
  }
};
